import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { CheckCircle2Icon, ShieldCheckIcon, TruckIcon, ThumbsUpIcon, MapPinIcon, PhoneIcon, CalendarCheckIcon, BuildingIcon, ArrowRightIcon } from 'lucide-react';
interface AboutPageProps {
  onNavigate: (page: string) => void;
}
export function AboutPage({
  onNavigate
}: AboutPageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const highlights = [{
    icon: TruckIcon,
    title: 'Free Delivery & Pickup',
    desc: 'To your hotel or residence'
  }, {
    icon: ShieldCheckIcon,
    title: 'Best Rate Guarantee',
    desc: 'We beat any advertised estimate'
  }, {
    icon: CheckCircle2Icon,
    title: 'Wide Selection',
    desc: 'Scooters, wheelchairs & more'
  }, {
    icon: ThumbsUpIcon,
    title: 'Satisfaction Guaranteed',
    desc: 'Top-rated customer service'
  }];
  const coverageAreas = ['Washington D.C.', 'Maryland', 'Baltimore', 'Northern Virginia'];
  return <main className="flex-grow w-full bg-white" data-id="element-503">
      <PageHeader title="About Us" breadcrumb="About Us" onNavigateHome={() => onNavigate('home')} data-id="element-504" />

      {/* Who We Are Section */}
      <section className="py-20 overflow-hidden" data-id="element-505">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-506">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-507">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-508">
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-6" data-id="element-509">
                Who We Are
              </h2>
              <div className="space-y-4 text-brand-charcoal/80 text-lg leading-relaxed" data-id="element-510">
                <p data-id="element-511">
                  Welcome to WheelAround Rentals, your trusted source for
                  mobility equipment rental in the District of Columbia Metro
                  area (DMV) and beyond.
                </p>
                <p data-id="element-512">
                  Whether you are a tourist visiting the Nation's capital for
                  business or pleasure, or a local in need of temporary mobility
                  assistance, we are here to help. We understand how important
                  it is to have access to reliable and comfortable mobility
                  equipment.
                </p>
                <p data-id="element-513">
                  That is why we offer a wide selection of electric scooters,
                  manual wheelchairs, power wheelchairs, and other mobility
                  aids. Our rental services cover Washington D.C., Maryland,
                  Baltimore, and Northern Virginia.
                </p>
                <p data-id="element-514">
                  We pride ourselves on providing exceptional customer service
                  and a satisfaction guarantee. We will deliver your chosen
                  mobility equipment to your desired location at no extra cost.
                  And if you need to be picked up from a different location, we
                  will do it for free too.
                </p>
                <p data-id="element-515">
                  We are committed to providing you with the best rental rates
                  in the city, and we will beat any advertised rental estimate.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-516">
              <img src="https://cdn.magicpatterns.com/uploads/eX9bxi46nQht2ufdroQH6Z/6-rfwu7jdi9ugdjjqqi1u1fo1vyfphtbhnxy7k3286bk.png" alt="Helping a customer with mobility assistance" className="rounded-3xl shadow-2xl object-cover w-full h-[500px]" data-id="element-517" />
              <div className="absolute -bottom-4 left-2 sm:-bottom-6 sm:-left-6 bg-brand-red text-white p-4 sm:p-6 rounded-2xl shadow-xl max-w-[220px] sm:max-w-[250px]" data-id="element-518">
                <div className="text-xl sm:text-2xl font-bold mb-2" data-id="element-519">
                  Committed to Care
                </div>
                <div className="text-xs sm:text-sm font-medium opacity-90" data-id="element-520">
                  Providing independence and mobility to our community.
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-brand-gray" data-id="element-521">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-522">
          <div className="text-center mb-16" data-id="element-523">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-4" data-id="element-524">
              Why Choose Us
            </h2>
            <p className="text-lg text-brand-charcoal/70 max-w-2xl mx-auto" data-id="element-525">
              We go above and beyond to ensure your mobility needs are met with
              the highest standards of service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-526">
            {highlights.map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow" data-id="element-527">
                <div className="w-16 h-16 mx-auto bg-brand-navy/5 rounded-full flex items-center justify-center text-brand-blue mb-6" data-id="element-528">
                  <item.icon className="w-8 h-8" data-id="element-529" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-3" data-id="element-530">
                  {item.title}
                </h3>
                <p className="text-brand-charcoal/70" data-id="element-531">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-20" data-id="element-532">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-533">
          <div className="bg-brand-navy rounded-3xl p-10 md:p-16 text-white text-center relative overflow-hidden" data-id="element-534">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
            backgroundSize: '30px 30px'
          }} data-id="element-535"></div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative z-10" data-id="element-536">
              <h2 className="text-3xl md:text-4xl font-extrabold mb-6" data-id="element-537">
                Our Coverage Areas
              </h2>
              <p className="text-lg text-white/80 max-w-2xl mx-auto mb-12" data-id="element-538">
                We proudly serve the entire DMV area, delivering mobility
                equipment directly to your location.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-539">
                {coverageAreas.map((area, index) => <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 flex flex-col items-center justify-center border border-white/20 hover:bg-white/20 transition-colors" data-id="element-540">
                    <MapPinIcon className="w-8 h-8 text-brand-red mb-3" data-id="element-541" />
                    <span className="font-bold text-lg" data-id="element-542">{area}</span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How to Make A Reservation */}
      <section className="py-20 bg-white overflow-hidden" data-id="element-543">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-544">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-545">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-546">
              <div className="flex items-center gap-3 mb-4" data-id="element-547">
                <div className="w-12 h-12 rounded-xl bg-brand-red/10 flex items-center justify-center" data-id="element-548">
                  <CalendarCheckIcon className="w-6 h-6 text-brand-red" data-id="element-549" />
                </div>
                <div className="h-[2px] w-8 bg-brand-red/30" data-id="element-550"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-6" data-id="element-551">
                How to Make A Reservation
              </h2>
              <div className="space-y-4 text-brand-charcoal/80 text-lg leading-relaxed" data-id="element-552">
                <p data-id="element-553">
                  To make a reservation for a scooter, wheelchair, or knee
                  walker, reach out to our team via phone by calling our
                  toll-free number at{' '}
                  <a href="tel:+18003954459" className="text-brand-red font-bold hover:underline" data-id="element-554">
                    +1(800)-395-4459
                  </a>{' '}
                  or{' '}
                  <a href="tel:+12023679965" className="text-brand-red font-bold hover:underline" data-id="element-555">
                    +1 (202)-367-9965
                  </a>
                  .
                </p>
                <p data-id="element-556">
                  Our friendly and knowledgeable representatives will assist you
                  in selecting the equipment that best fits your needs and
                  provide accurate information on what we have in stock.
                </p>
                <p data-id="element-557">
                  Additionally, you can easily reserve your equipment online by
                  visiting our dedicated reservation page.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-8" data-id="element-558">
                <button onClick={() => onNavigate('reserve')} className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-md cursor-pointer" data-id="element-559">
                  Reserve Online
                  <ArrowRightIcon className="w-4 h-4" data-id="element-560" />
                </button>
                <a href="tel:+18003954459" className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy/5 text-brand-navy rounded-xl font-bold hover:bg-brand-navy/10 transition-colors cursor-pointer" data-id="element-561">
                  <PhoneIcon className="w-4 h-4" data-id="element-562" />
                  Call Us Now
                </a>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative" data-id="element-563">
              <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Making a reservation" className="rounded-3xl shadow-2xl object-cover w-full h-[420px]" data-id="element-564" />
              <div className="absolute -bottom-3 right-2 sm:-bottom-5 sm:-right-5 bg-white p-4 sm:p-5 rounded-2xl shadow-xl border border-gray-100" data-id="element-565">
                <div className="flex items-center gap-3" data-id="element-566">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-567">
                    <CheckCircle2Icon className="w-5 h-5 text-emerald-600" data-id="element-568" />
                  </div>
                  <div data-id="element-569">
                    <div className="text-sm font-bold text-brand-navy" data-id="element-570">
                      Easy Booking
                    </div>
                    <div className="text-xs text-brand-charcoal/50" data-id="element-571">
                      Online or by phone
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hotel Rentals */}
      <section className="py-20 bg-brand-gray overflow-hidden" data-id="element-572">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-573">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-574">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1 relative" data-id="element-575">
              <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Hotel entrance" className="rounded-3xl shadow-2xl object-cover w-full h-[480px]" data-id="element-576" />
              <div className="absolute -top-3 left-2 sm:-top-5 sm:-left-5 bg-brand-navy text-white p-4 sm:p-5 rounded-2xl shadow-xl" data-id="element-577">
                <div className="flex items-center gap-3" data-id="element-578">
                  <TruckIcon className="w-6 h-6 text-brand-red" data-id="element-579" />
                  <div data-id="element-580">
                    <div className="text-sm font-bold" data-id="element-581">Free Hotel Delivery</div>
                    <div className="text-xs opacity-70" data-id="element-582">
                      D.C., MD, VA & Baltimore
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="order-1 lg:order-2" data-id="element-583">
              <div className="flex items-center gap-3 mb-4" data-id="element-584">
                <div className="w-12 h-12 rounded-xl bg-brand-blue/10 flex items-center justify-center" data-id="element-585">
                  <BuildingIcon className="w-6 h-6 text-brand-blue" data-id="element-586" />
                </div>
                <div className="h-[2px] w-8 bg-brand-blue/30" data-id="element-587"></div>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-navy mb-6" data-id="element-588">
                Hotel Rentals
              </h2>
              <div className="space-y-4 text-brand-charcoal/80 text-lg leading-relaxed" data-id="element-589">
                <p data-id="element-590">
                  Welcome to WheelAround Rentals, where we provide convenient
                  and reliable mobility solutions for travelers. We understand
                  that when traveling, mobility can be a concern, and that's
                  where we come in.
                </p>
                <p data-id="element-591">
                  Our rental services provide a wide selection of electric
                  scooters, manual wheelchairs, power wheelchairs, and other
                  mobility aids to suit your needs. Our rental services cover
                  Washington D.C., Maryland, Baltimore, and Northern Virginia,
                  and we offer delivery services to various hotels in these
                  areas.
                </p>
                <p data-id="element-592">
                  All you need to do is let us know your hotel's name and
                  address, and we will deliver the equipment to your doorstep.
                  Our rental services are available to all hotel guests, whether
                  you're in town for business or leisure.
                </p>
                <p data-id="element-593">
                  We provide exceptional customer service and a satisfaction
                  guarantee to ensure that your rental experience with us is
                  hassle-free and comfortable. Trust us to provide you with
                  quality service and equipment during your stay.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 mt-8" data-id="element-594">
                <button onClick={() => onNavigate('hotel-rentals')} className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-white rounded-xl font-bold hover:bg-brand-navy/90 transition-colors shadow-md cursor-pointer" data-id="element-595">
                  View Hotel Rentals
                  <ArrowRightIcon className="w-4 h-4" data-id="element-596" />
                </button>
                <button onClick={() => onNavigate('contact')} className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-navy rounded-xl font-bold hover:bg-gray-50 transition-colors border border-gray-200 cursor-pointer" data-id="element-597">
                  Contact Us
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-white border-t border-gray-100" data-id="element-598">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-599">
          <h2 className="text-3xl font-bold text-brand-navy mb-6" data-id="element-600">
            Ready to get started?
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-601">
            <button onClick={() => onNavigate('reserve')} className="px-8 py-4 bg-brand-red text-white rounded-full font-bold text-lg hover:bg-red-700 transition-all shadow-lg" data-id="element-602">
              Reserve Now
            </button>
            <a href="tel:+18003954459" className="px-8 py-4 bg-white text-brand-navy border-2 border-brand-navy rounded-full font-bold text-lg hover:bg-brand-navy hover:text-white transition-all flex items-center justify-center gap-2" data-id="element-603">
              <PhoneIcon className="w-5 h-5" data-id="element-604" />
              Call (800) 395-4459
            </a>
          </div>
        </div>
      </section>
    </main>;
}